// PanelBreakSheet — give a break, and record who actually gave it.
//
// ⚠️ THE REASON THIS IS NOT ONE BUTTON.
//
// `coverBreak()` auto-captures the SIGNED-IN user as the person covering. On a
// personal phone that is right and it is why the board card uses it. On this
// panel it is wrong: the panel runs on a shared workstation and, in time, on a
// wall device whose account is not a person. Pressing a one-click "cover" there
// records the WALL as the CRNA who relieved someone for lunch — a break with
// the wrong cover written against it is a break nobody is actually giving, and
// the request clears so nobody comes.
//
// So this asks two questions and answers neither by default: which break, and
// who is covering it. `assignBreak({ channel, giver })` takes an explicit giver
// precisely so a shared device can name a real person.
//
// The board card's existing behaviour is untouched. This is the shared-device
// path, not a replacement.

import React, { useEffect, useRef, useState } from "react";
import styles, { C } from "./panelCard.styles";
import { BREAK_CHANNELS, getChannelState } from "../../domain/breaks";

const CHANNEL_LABEL = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", bathroom: "Bathroom" };

// A meal channel already done is locked by the engine; offering it invites a
// write that the state machine will refuse.
const LOCKED = new Set(["done", "in_progress"]);

export default function PanelBreakSheet({
  lane,
  breakRow,
  candidates = [],
  busy = false,
  error = null,
  onCommit,
  onClose,
}) {
  const [channel, setChannel] = useState(null);
  const [giver, setGiver] = useState(null);
  const headingRef = useRef(null);

  useEffect(() => { if (headingRef.current) headingRef.current.focus(); }, []);

  const breaks = breakRow ? breakRow.breaks : null;
  const canCommit = Boolean(channel && giver && !busy);

  return (
    // Sheet container with onKeyDown — the keyboard path, not a mouse-only trap.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <section
      onKeyDown={(e) => { if (e.key === "Escape" && !busy) { e.stopPropagation(); onClose(); } }}
      aria-label="Give a break"
      data-testid="break-sheet"
      style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: C.radius, overflow: "hidden" }}
    >
      <header style={{ padding: "10px 12px", borderBottom: `1px solid ${C.line}`, background: "#f8fafc" }}>
        <div style={{ fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: C.mutedSoft }}>
          ROOM · {lane?.roomName}
        </div>
        <h2 ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 15, outline: "none" }}>
          Give a break
        </h2>
      </header>

      {error ? (
        <div style={{ padding: "8px 12px", background: C.alarmBg, borderBottom: `2px solid ${C.alarm}`, color: "#7f1d1d", fontWeight: 700 }}>
          {error}
        </div>
      ) : null}

      <div style={{ padding: 12, display: "grid", gap: 12 }}>
        <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
          <legend style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: C.muted, paddingBottom: 6 }}>
            Which break
          </legend>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 }}>
            {BREAK_CHANNELS.map((ch) => {
              const state = getChannelState(breaks, ch);
              const locked = LOCKED.has(state);
              const selected = channel === ch;
              return (
                <button
                  key={ch}
                  type="button"
                  onClick={() => !locked && setChannel(ch)}
                  aria-pressed={selected}
                  aria-disabled={locked ? "true" : undefined}
                  style={{
                    ...styles.act,
                    minHeight: 40,
                    textAlign: "left",
                    opacity: locked ? 0.5 : 1,
                    cursor: locked ? "not-allowed" : "pointer",
                    ...(selected ? { background: C.navySoft, borderColor: C.navy, color: C.navy } : null),
                  }}
                >
                  {CHANNEL_LABEL[ch]}
                  {locked ? <span style={{ display: "block", fontSize: 10.5, fontWeight: 400 }}>{state === "done" ? "already done" : "on break"}</span> : null}
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
          <legend style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: C.muted, paddingBottom: 6 }}>
            Who is covering
          </legend>
          {candidates.length === 0 ? (
            <p style={{ margin: 0, fontSize: 12.5, color: C.muted }}>
              Nobody is free to cover. Place someone on the bench first.
            </p>
          ) : (
            <div style={{ display: "grid", gap: 5, maxHeight: 190, overflowY: "auto" }}>
              {candidates.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setGiver(p)}
                  aria-pressed={giver?.id === p.id}
                  style={{
                    ...styles.act,
                    minHeight: 40,
                    textAlign: "left",
                    ...(giver?.id === p.id ? { background: C.navySoft, borderColor: C.navy, color: C.navy } : null),
                  }}
                >
                  {p.name}
                  <span style={{ color: C.muted, fontWeight: 400 }}> · {String(p.subRole || "CRNA").toUpperCase()}</span>
                </button>
              ))}
            </div>
          )}
        </fieldset>
      </div>

      <footer style={{ display: "flex", gap: 8, padding: "10px 12px", borderTop: `1px solid ${C.line}` }}>
        <button type="button" onClick={onClose} style={{ ...styles.act, marginLeft: "auto" }}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => canCommit && onCommit({ channel, giver })}
          aria-disabled={!canCommit ? "true" : undefined}
          style={{
            ...styles.act,
            ...(canCommit ? styles.actPrimary : { background: "#94a3b8", borderColor: "#94a3b8", color: "#fff", cursor: "not-allowed" }),
          }}
        >
          {busy ? "Saving…" : "Give break"}
        </button>
      </footer>
    </section>
  );
}
